<template>
  <article ref="component" class="teaser-list-item">
    <component
      :is="props.tag === 'button' ? 'button' : NuxtLink"
      class="teaser-list-item-link"
      :class="computedPropStyling"
      :href="newTo"
      :target="state.target"
      :rel="state.rel"
      :aria-label="state.title"
      @click="handleClick"
      @click.middle="handleClick"
    >
      <div class="teaser-list-item-image-container" :style="`aspect-ratio: ${props.aspectRatio}`">
        <DynamicImage
          class="teaser-list-item-image"
          :image="state.image"
          :aspect-ratio="props.aspectRatio"
          :renditions="teaserListItemRendition"
          :loading="loading"
          :priority="priority"
          :fluid="true"
        />
        <div class="teaser-list-item-badge-wrapper">
          <Badge
            v-if="isVideo"
            :title="computedTitle"
            icon="eenvandaag-general:play-solid"
            size="small"
          />
          <Badge v-if="state.external" icon="eenvandaag-general:external-2" size="small" />
        </div>
      </div>
      <div class="teaser-list-item-content" :style="{ 'justify-content': justifyContent }">
        <Label
          v-if="computedShowLabel"
          size="medium"
          :title="state.label"
          :variant="props.articleType === 'shorts' ? 'darker' : undefined"
        />
        <Heading
          v-if="state.title && !ariaHidden"
          class="teaser-list-item-title"
          :content="state.title"
          :level="headingLevel"
          :max-lines="state.label ? 5 : 6"
        />
        <Paragraph
          v-if="showIntro && computedIntro"
          :content="computedIntro"
          class="teaser-list-item-description"
        />

        <DateTime v-if="!state.external && showDate" v-bind="props" class="teaser-list-item-meta" />
      </div>
    </component>
  </article>
</template>

<script setup lang="ts">
import type {
  ITeaser,
  IAnalytics,
  IImageRendition,
  IImageLoadingSettings,
  ICollectionTeaser,
} from '~~/types/local_types';
import DateTime from '~/components/DateTime.vue';
import useLoadMoreNuxtLink from '~/composables/useLoadMoreNuxtLink';
import { NuxtLink } from '#components';
import formatUtils from '~/utils/formatUtils';

interface IProps extends IImageLoadingSettings, ICollectionTeaser {
  headingLevel?: number;
  showDate?: boolean;
  analytics?: IAnalytics;
  padded?: boolean;
  showIntro?: boolean;
  showLabel?: boolean;
  ariaHidden?: boolean;
  tag?: 'button' | 'a';
  aspectRatio?: '4x3' | '3x4';
  justifyContent?: 'flex-start' | 'center';
}

const props = withDefaults(defineProps<IProps>(), {
  headingLevel: 3,
  analytics: undefined,
  showDate: false,
  showLabel: true,
  loading: 'lazy',
  priority: 'low',
  padded: true,
  showIntro: false,
  ariaHidden: false,
  tag: 'a',
  aspectRatio: '4x3',
  justifyContent: 'flex-start',
});

const state = useArticleData(props as ITeaser);
const component = ref<HTMLElement | null>(null);
const { formatIsoDurationNoZeros } = formatUtils();

const { url: newTo } = useLoadMoreNuxtLink(state.url);

const handleClick = () => {
  const analyticsDefaults = {
    ...props.analytics,
    linkText: props.title,
    linkUrl: state.url,
  };
  gtmTrackEvent({ ...analyticsDefaults, ...props?.analytics });
};

const teaserListItemRendition: IImageRendition[] = [
  { breakpoint: 'sm', size: 160, aspectRatio: props.aspectRatio },
];

const computedPropStyling = computed(() => {
  return props.padded ? 'is--padded' : '';
});

const computedIntro = computed(() => {
  return props.description || props.teaserText || state.intro;
});

const computedShowLabel = computed(() => {
  return props.showLabel && state.label && state.label !== 'artikel';
});

const computedTitle = computed(() => {
  if (!props.mainEmbed || !props.mainEmbed[0]) return;
  return formatIsoDurationNoZeros(props.mainEmbed[0]?.contentEmbedsId?.duration);
});
</script>

<style lang="postcss" scoped>
.teaser-list-item-link {
  --teaser-list-item-link-gap: var(--unit-12);

  /* unset default button classes (when button) */
  padding: 0;
  background-color: transparent;
  border: unset;
  text-align: left;
  cursor: pointer;

  display: flex;
  text-decoration: none;
  outline: none;
  gap: var(--teaser-list-item-link-gap);

  @media (--lg-viewport) {
    --teaser-list-item-link-gap: var(--unit-16);
  }

  @add-mixin hover {
    .teaser-list-item-image {
      scale: 1.05;
    }

    .teaser-list-item-title {
      color: hsl(var(--color-primary-600));
    }
  }

  &:active {
    .teaser-list-item-title {
      color: hsl(var(--color-primary-800));
    }
  }

  @add-mixin focus light {
  }

  &.is--padded {
    --teaser-list-item-margin: 0 var(--unit-18);
    margin: var(--teaser-list-item-margin);

    @media (--lg-viewport) {
      --teaser-list-item-margin: 0 var(--unit-32);
    }
  }
}

.teaser-list-item-image-container {
  --teaser-list-image-width: 92px;
  --teaser-list-image-height: 70px;

  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  align-self: flex-start;
  width: var(--teaser-list-image-width);
  height: var(--teaser-list-image-height);

  @media (--lg-viewport) {
    --teaser-list-image-width: 149px;
    --teaser-list-image-height: 112px;

    align-items: center;
  }
}

.teaser-list-item-badge-wrapper {
  line-height: 0;
  --teaser-list-badge-wrapper-padding: var(--unit-4);
  position: absolute;
  bottom: 0;
  left: 0;
  padding: var(--teaser-list-badge-wrapper-padding);

  @media (--md-viewport) {
    --teaser-list-badge-wrapper-padding: var(--unit-8);
  }
}

.teaser-list-item-image {
  transition: scale 250ms ease-in;
  transform-origin: center center;
}

.teaser-list-item-content {
  --teaser-list-content-gap: var(--unit-4);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--teaser-list-content-gap);

  @media (--lg-viewport) {
    --teaser-list-content-padding: 0 0 0 0;
    justify-content: center;
  }
}

.teaser-list-item-title {
  color: hsl(var(--color-primary-950));
  transition: color 250ms ease-in;

  @add-mixin title-sm;
}

.teaser-list-item-meta {
  color: hsl(var(--color-primary-600));

  @add-mixin label-md;
}

.teaser-list-item-description {
  color: hsl(var(--color-primary-900));
  display: none;

  @add-mixin body-md;

  @media (--lg-viewport) {
    display: block;
    @add-mixin clamp 3;
  }
}
</style>
